import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, MapPin, Box } from 'lucide-react';

/**
 * ZENTRALER DIALOG FÜR UNBEKANNTE QR-CODES
 * 
 * Wird angezeigt wenn ein gescannter QR-Code weder als Standort noch als Automat gefunden wird.
 * Bietet einheitliche Optionen zum Anlegen eines neuen Standorts oder Automaten.
 * 
 * Props:
 * - open: boolean - Dialog sichtbar?
 * - onOpenChange: (open: boolean) => void - Callback zum Schließen
 * - scannedCode: string - Der gescannte Code
 * - onCreateStandort: () => void - Callback für "Neuer Standort"
 * - onCreateAutomat: () => void - Callback für "Neuer Automat"
 * - showStandortOption: boolean - Zeige Standort-Option (nur für Admin)
 * 
 * @version 1.6
 */
const UnknownCodeDialog = ({
  open,
  onOpenChange,
  scannedCode,
  onCreateStandort,
  onCreateAutomat,
  showStandortOption = true
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="unknown-code-dialog">
        <DialogHeader>
          <DialogTitle>Code nicht gefunden</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center space-x-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="text-amber-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-medium text-amber-900">Gescannter Code:</p>
              <p className="text-sm text-amber-700 break-all">{scannedCode}</p>
            </div>
          </div>
          <p className="text-slate-600 mb-4">
            {showStandortOption 
              ? 'Dieser Code wurde nicht gefunden. Möchtest du ein neues Gehäuse (Standort) oder einen neuen Automaten anlegen?'
              : 'Dieser Code wurde nicht gefunden. Möchtest du einen neuen Automaten anlegen?'
            }
          </p>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          {showStandortOption && (
            <Button
              onClick={onCreateStandort}
              className="flex-1"
              variant="outline"
              data-testid="create-standort-option"
            >
              <MapPin size={16} className="mr-2" />
              Neues Gehäuse (Standort)
            </Button>
          )}
          <Button
            onClick={onCreateAutomat}
            className="flex-1"
            data-testid="create-automat-option"
          >
            <Box size={16} className="mr-2" />
            Neuer Automat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnknownCodeDialog;
